import { ForwardedRef, forwardRef } from 'react';
import { MessageBox } from 'react-chat-elements';
import styles from './Message.module.scss';
import cx from 'classnames';
import { Icon, Text } from 'components';
import { useDevice, useDoubleTap } from 'hooks';
import { patientsActions } from 'store/patients/slice';
import { useDispatch } from 'store/utils';
import { MessagesType, TypeOfMessages } from 'types/messages';

type Props = {
  message: MessagesType;
  position: 'left' | 'right';
  role: 'Studio' | 'Doctor' | undefined;
  name: string | undefined;
  onlineStatus: 'off' | 'online' | 'offline';
  isEnabled: boolean;
  currentUserId: string | undefined;
  isParticipantOfConversation: boolean;
};

const messagePropsBinded = {
  forwarded: false,
  removeButton: false,
  notch: false,
  retracted: false,
  replyButton: false,
  focus: false,
  titleColor: 'black',
};

export const Message = forwardRef(
  (
    { message, position, name, role, onlineStatus, isEnabled, currentUserId, isParticipantOfConversation }: Props,
    ref: ForwardedRef<HTMLDivElement>,
  ) => {
    const [firstName, surname] = name?.split(' ') ?? [];
    const initials = (firstName?.[0] ?? '') + (surname ? surname?.[0] : '');
    const { isPhone, isTablet } = useDevice();
    const dispatch = useDispatch();

    const doubleTapAction = useDoubleTap(() => {
      sendLike();
    });

    const messageProps = {
      position,
      title: `${name?.toUpperCase()}\n${role?.[0].toLocaleUpperCase()}${role?.slice(1)}`,
      date: new Date(message.createdAt + 'Z'),
      id: message.id,
      status: (message.isRead ? 'received' : 'sent') as 'received' | 'sent',
    };

    const isLiked = message.reactions[0]?.reactions.includes('like');

    const isAllowToSendReaction = isEnabled && message.createdBy !== currentUserId;

    const sendLike = () => {
      isAllowToSendReaction &&
        dispatch(
          patientsActions.sendPatientMessageReaction({
            messageId: message.id,
            patientCaseId: message.patientCaseId,
            reactions: { reactions: isLiked ? [] : ['like'] },
          }),
        );
    };

    const renderMessage = (message: MessagesType) => {
      switch (message.type) {
        case TypeOfMessages.TEXT: {
          return <MessageBox type="text" text={message.body} {...messageProps} {...messagePropsBinded} />;
        }
        case TypeOfMessages.LINK: {
          return (
            <MessageBox
              type="file"
              text={message.attachmentLink}
              onDownload={() => window.open(message.attachmentLink, '_blank')}
              data={{
                uri: message.attachmentLink,
                status: {
                  loading: false,
                  autoDownload: false,
                  download: false,
                },
              }}
              {...messageProps}
              {...messagePropsBinded}
            />
          );
        }
        case TypeOfMessages.PHOTO: {
          return (
            <MessageBox
              type="photo"
              text={''}
              onOpen={() => window.open(message.attachmentFile.url, '_blank')}
              data={{
                uri: message.attachmentFile.url,
                width: isPhone ? 267 : 308,
                height: 260,
                status: {
                  loading: true,
                  autoDownload: true,
                  download: true,
                  click: false,
                  error: false,
                },
              }}
              {...messageProps}
              {...messagePropsBinded}
            />
          );
        }
        case TypeOfMessages.FILE: {
          return (
            <MessageBox
              type="file"
              text={message.attachmentFile.name}
              onDownload={() => window.open(message.attachmentFile.url, '_blank')}
              data={{
                uri: message.attachmentFile.url,
                status: {
                  loading: false,
                  autoDownload: false,
                  download: false,
                },
              }}
              {...messageProps}
              {...messagePropsBinded}
            />
          );
        }
        default: {
          return null;
        }
      }
    };

    return (
      <div className={cx(styles.container, styles[position])} ref={ref}>
        <div className={cx(styles.userPic, styles[position])}>
          <Text type="button">{initials}</Text>
          {onlineStatus !== 'off' && (
            <div className={cx(styles.onlineStatusDot, onlineStatus === 'offline' ? styles.Offline : styles.Online)} />
          )}
        </div>
        <div
          className={cx(styles.message, styles[position], styles[message.type.toLowerCase()], {
            [styles.sent]: message.isRead,
            [styles.liked]: isLiked,
            [styles.reactionAction]: isAllowToSendReaction,
            [styles.hideCheckmark]: isParticipantOfConversation,
          })}
          {...(isTablet && isAllowToSendReaction ? doubleTapAction : {})}>
          {renderMessage(message)}
          <div className={styles.likeWrapper} onClick={sendLike}>
            <Icon name={isLiked ? 'Liked' : 'Like'} className={styles.like} />
          </div>
        </div>
      </div>
    );
  },
);

Message.displayName = 'Message';
